import './index.less'
import './index.less'
import './reset-v-page-builder.less'

// Configuration object for easy maintenance
const CONFIG = {
  selectors: {
    tabBox: '.product-tabs-box',
    header: '.jky-header',
    headerAftersales: '.header-after-sales',
    pcNavList: '.subnav-list',
    pcNavItem: '.subnav-list .navli',
    mobileTabs: '.jky-tabs-scroll-inner .jky-tabs-item',
    mobileContainer: '.jky-tabs-scroll-inner',
    mobileInnerWrapper: '.jky-tabs-scroll-inner .inner-w',
    mobileNext: '.next-action',
    mobilePrev: '.prev-action',
    mobileUnderline: '.jky-tabs-slideline .jky-tabs-slideline--inner',
    pcUnderline: '.subnav-list .slider-main',
    productName: '.product-detail-variants .product-tabs-container .product-name',
    container: '.product-tabs-container'
  },
  durations: {
    scroll: 300,
    layoutCheck: 100,
    layoutCheckInterval: 50,
    hashDelay: 200,
    scrollVerification: 250,
    scrollCorrection: 100,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  thresholds: {
    maxLayoutAttempts: 10,
    scrollDifference: 20,
    mobileWidth: 1024,
    highlightRange: [0, 300]
  },
  isTabClick: false,
};

// Layout stability checker
function waitForLayoutStable(target, callback) {
  let attempts = 0;
  let lastOffset = target.offsetTop;

  const checkPosition = () => {
    const currentOffset = target.offsetTop;
    if (currentOffset === lastOffset || attempts >= CONFIG.thresholds.maxLayoutAttempts) {
      callback(currentOffset);
      return;
    }
    lastOffset = currentOffset;
    attempts++;
    requestAnimationFrame(() => setTimeout(checkPosition, CONFIG.durations.layoutCheckInterval));
  };

  setTimeout(checkPosition, CONFIG.durations.layoutCheck);
}

class TabNavigation {
  constructor() {
    this.isMobile = () => window.innerWidth < CONFIG.thresholds.mobileWidth;
    this.anchorNav = document.querySelector(CONFIG.selectors.tabBox);
    if (!this.anchorNav) return;

    this.navHeight = this.anchorNav.offsetHeight;
    this.navTop = this.anchorNav.offsetTop;
    this.placeholder = this.createPlaceholder();
    this.init();
  }

  createPlaceholder() {
    const placeholder = document.createElement('div');
    placeholder.style.height = `${this.navHeight}px`;
    placeholder.style.display = 'none';
    this.anchorNav.parentNode.insertBefore(placeholder, this.anchorNav);
    return placeholder;
  }

  getAnchors() {
    const selector = this.isMobile() ? CONFIG.selectors.mobileTabs : CONFIG.selectors.pcNavItem;
    const navItems = document.querySelectorAll(selector);
    return Array.from(navItems)
      .map(item => item.getAttribute('data-cate'))
      .filter(Boolean);
  }

  adjustHeader() {
    const targetTop = this.anchorNav.getBoundingClientRect().top + window.scrollY;
    const header = document.querySelector(CONFIG.selectors.header || 'header-v5');
    const headerAftersales = document.querySelector(CONFIG.selectors.headerAftersales);
    const headerHeight = header?.offsetHeight || 0;
    const headerAftersalesHeight = headerAftersales?.offsetHeight || 0;

    if (this.isMobile() && document.querySelector('.shopify-section.jky-header')?.classList?.contains?.('menu-active--open')) {
      return;
    }

    if (targetTop - window.scrollY <= headerHeight + headerAftersalesHeight) {
      const top = targetTop - window.scrollY - headerHeight - headerAftersalesHeight;
      if (header) header.style.top = `${top}px`;
    } else {
      if (header) header.style.top = '0px';
      if (!CONFIG.isTabClick) history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  highlightAnchor(anchor) {
    const selector = this.isMobile() ? CONFIG.selectors.mobileTabs : CONFIG.selectors.pcNavItem;
    document.querySelectorAll(`${selector}.active`).forEach(item => item.classList.remove('active'));

    const activeItem = document.querySelector(`${selector}[data-cate="${anchor}"]`);
    if (activeItem) {
      activeItem.classList.add('active');
      if (this.isMobile()) {
        const customElement = document.querySelector('jky-v5-pdp-tabs');
        requestAnimationFrame(() => {
          customElement?.setTabItemUnderline()
          customElement?.watchNavScroll()
          scrollContentXSmoothly(activeItem, customElement?.selectors?.container);
        });
      } else {
        requestAnimationFrame(() => this.setUnderline());
      }
    }
  }

  updateAnchorHighlight() {
    const anchors = this.getAnchors();
    const rects = anchors.map(anchor => document.querySelector(`[data-anchor-v5="${anchor}"]`)?.getBoundingClientRect());

    for (let i = 0; i < anchors.length; i++) {
      const target = document.querySelector(`[data-anchor-v5="${anchors[i]}"]`);
      if (!target) continue;

      const rect = rects[i];
      if (rect.top + this.navHeight >= CONFIG.thresholds.highlightRange[0] &&
          rect.top + this.navHeight <= CONFIG.thresholds.highlightRange[1] && !CONFIG.isTabClick) {
        this.highlightAnchor(anchors[i]);
        break;
      }
      if (rect.top + this.navHeight < CONFIG.thresholds.highlightRange[0] &&
          rects[i + 1]?.top >= window.innerHeight && !CONFIG.isTabClick) {
        this.highlightAnchor(anchors[i]);
        break;
      }
    }
  }

  handleScroll() {
    this.navTop = Math.max(this.anchorNav.offsetTop, this.navTop);
    this.adjustHeader();

    if (window.scrollY >= this.navTop) {
      this.anchorNav.classList.add('fixed');
      this.placeholder.style.display = 'block';
    } else {
      this.anchorNav.classList.remove('fixed');
      this.placeholder.style.display = 'none';
    }

    this.updateAnchorHighlight();
  }

  handleResize() {
    this.navHeight = this.anchorNav.offsetHeight;
    this.placeholder.style.height = `${this.navHeight}px`;
  }

  setUnderline() {
    const underline = document.querySelector(CONFIG.selectors.pcUnderline);
    const activeTab = document.querySelector(`${CONFIG.selectors.pcNavItem}.active`);
    if (!underline || !activeTab) return;

    Object.assign(underline.style, {
      width: `${activeTab.offsetWidth}px`,
      left: `${activeTab.offsetLeft}px`,
      transition: CONFIG.durations.transition
    });
  }

  init() {
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    window.addEventListener('resize', debounce(() => this.handleResize(), 250), { passive: true });
  }
}

class DesktopTabs extends TabNavigation {
  constructor() {
    super();
    if (!this.anchorNav || this.isMobile()) return;
    this.initDesktop();
  }

  handleTabClick(event) {
    const clickedTab = event.target.closest('.navli');
    if (!clickedTab) return;

    document.querySelectorAll(CONFIG.selectors.pcNavItem).forEach(tab => tab.classList.remove('active'));
    clickedTab.classList.add('active');
    this.setUnderline();

    const targetAnchor = clickedTab.getAttribute('data-cate');
    const target = document.querySelector(`[data-anchor-v5="${targetAnchor}"]`);
    if (!target) return;

    CONFIG.isTabClick = true;

    const performScroll = () => {
      const containerHeight = document.querySelector(CONFIG.selectors.container)?.offsetHeight || 0;
      const targetY = target.offsetTop - containerHeight;

      $('html, body').animate({ scrollTop: targetY }, CONFIG.durations.scroll);

      setTimeout(() => {
        const currentY = window.scrollY;
        const actualTargetY = target.offsetTop - containerHeight;
        const difference = Math.abs(currentY - actualTargetY);

        if (difference > CONFIG.thresholds.scrollDifference) {
          $('html, body').animate({ scrollTop: actualTargetY }, CONFIG.durations.scrollCorrection, () => {
            CONFIG.isTabClick = false;
          });
        } else {
          setTimeout(() => CONFIG.isTabClick = false, 50);
        }
      }, CONFIG.durations.scrollVerification);
    };

    waitForLayoutStable(target, () => performScroll());
    history.pushState(null, null, `#${targetAnchor}`);
  }

  handleHash() {
    const hash = location.hash.replace('#', '');
    if (hash && hash.includes('product_tab_')) {
      const targetTab = document.querySelector(`${CONFIG.selectors.pcNavItem}[data-cate="${hash}"]`);
      if (targetTab) targetTab.click();
    }
  }

  handleVariantChange(e) {
    if (!e.detail?.variant?.option1) return;
    const productName = document.querySelector(CONFIG.selectors.productName);
    const navItems = document.querySelectorAll(CONFIG.selectors.pcNavItem);

    if (productName) productName.textContent = e.detail.variant.option1;
    navItems.forEach(item => item.setAttribute('data-ga-params-content', e.detail.variant.option1));
  }

  initDesktop() {
    this.setUnderline();
    const navList = document.querySelector(CONFIG.selectors.pcNavList);
    if (navList) navList.addEventListener('click', (e) => this.handleTabClick(e));
    setTimeout(() => this.handleHash(), CONFIG.durations.hashDelay);
    document.addEventListener('variantChangeEvent', (e) => this.handleVariantChange(e));
  }
}

class MobileTabs extends HTMLElement {
  constructor() {
    super();
    this.activeTabIndex = 0;
    this.selectors = {
      container: this.querySelector(CONFIG.selectors.mobileContainer),
      next: this.querySelector(CONFIG.selectors.mobileNext),
      prev: this.querySelector(CONFIG.selectors.mobilePrev),
      innerWrapper: this.querySelector(CONFIG.selectors.mobileInnerWrapper),
      tabItems: this.querySelectorAll(CONFIG.selectors.mobileTabs),
      underline: this.querySelector(CONFIG.selectors.mobileUnderline)
    };
    this.init();
  }

  init() {
    if (this.selectors.container.scrollWidth > this.selectors.container.offsetWidth) {
      this.selectors.next?.classList?.remove('opacity-0', 'invisible');
    }

    this.setTabItemUnderline();
    this.selectors.tabItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        this.activeTabIndex = index;
        this.handleTabClick(item);
      });
    });

    this.selectors.prev?.addEventListener('click', () => this.slidePrev());
    this.selectors.next?.addEventListener('click', () => this.slideNext());
    setTimeout(() => this.handleHash(), CONFIG.durations.hashDelay);
  }

  handleTabClick(item) {
    this.selectors.tabItems.forEach(tab => tab.classList.remove('curr_tab', 'active'));
    item.classList.add('active', 'curr_tab');
    this.setTabItemUnderline();

    this.scrollToAnchor(item);
    
    scrollContentXSmoothly(item, this.selectors.container);
  }

  scrollToAnchor(item) {
    const targetAnchor = item.getAttribute('data-cate');
    const target = document.querySelector(`[data-anchor-v5="${targetAnchor}"]`);
    if (!target) return;

    CONFIG.isTabClick = true

    const performScroll = () => {
      const containerHeight = document.querySelector(CONFIG.selectors.container)?.offsetHeight || 0;
      const targetY = target.offsetTop - containerHeight;

      $('html, body').animate({ scrollTop: targetY }, CONFIG.durations.scroll);

      setTimeout(() => {
        const currentY = window.scrollY;
        const actualTargetY = target.offsetTop - containerHeight;
        const difference = Math.abs(currentY - actualTargetY);

        if (difference > CONFIG.thresholds.scrollDifference) {
          $('html, body').animate({ scrollTop: actualTargetY }, CONFIG.durations.scrollCorrection, () => {
            CONFIG.isTabClick = false;
            this.watchNavScroll();
          });
        } else {
          setTimeout(() => {
            CONFIG.isTabClick = false;
            this.watchNavScroll();
          }, 50);
        }
      }, CONFIG.durations.scrollVerification);
    };

    waitForLayoutStable(target, () => performScroll());
    history.pushState(null, null, `#${targetAnchor}`);
  }

  slidePrev() {
    const index = Math.max(0, this.activeTabIndex - 1);
    this.slideTo(index);
  }

  slideNext() {
    const index = Math.min(this.activeTabIndex + 1, this.selectors.tabItems.length - 1);
    this.slideTo(index);
  }

  slideTo(index) {
    this.activeTabIndex = index;
    this.selectors.tabItems[index].click();
  }

  watchNavScroll() {
    const scrollLeft = this.selectors.container.scrollLeft;
    const scrollWidth = this.selectors.container.scrollWidth;
    const offsetWidth = this.selectors.container.offsetWidth;

    if (scrollLeft === 0) {
      this.selectors.prev?.classList?.add('invisible');
      this.selectors.next?.classList?.remove('invisible');
    } else if (scrollLeft + offsetWidth >= scrollWidth - 1) {
      this.selectors.prev?.classList?.remove('invisible');
      this.selectors.next?.classList?.add('invisible');
    } else {
      this.selectors.prev?.classList?.remove('invisible');
      this.selectors.next?.classList?.remove('invisible');
    }
  }

  setTabItemUnderline() {
    const underline = this.selectors.underline;
    const activeTab = this.querySelector(`${CONFIG.selectors.mobileTabs}.active`);
    if (!underline || !activeTab) return;

    underline.style.width = `${activeTab.offsetWidth}px`;
    underline.style.left = `${activeTab.offsetLeft}px`;
  }

  handleHash() {
    const hash = location.hash.replace('#', '');
    if (hash && hash.includes('product_tab_')) {
      const targetTab = this.querySelector(`${CONFIG.selectors.mobileTabs}[data-cate="${hash}"]`);
      if (targetTab) targetTab.click();
    }
  }
}

customElements.define('jky-v5-pdp-tabs', MobileTabs);

document.addEventListener('DOMContentLoaded', () => {
  new DesktopTabs();
});
